import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"

export type ParticipantRole = "reader" | "editor" | "admin" | "owner"

export type Participant = {
  oid: string
  displayName: string
  type: "user" | "group"
  role: ParticipantRole
  isOriginalOwner?: boolean
  /** Durable marker stamped on the demoted owner when ownership is transferred away. */
  isFormerOwner?: boolean
}

export type ParticipantsResponse = {
  participants: Participant[]
  currentUserRole: ParticipantRole
  ownerUserId: string
}

export type TrustedSourceRef = {
  name: string
  url: string
}

export type Project = {
  id: string
  name?: string
  sessionType?: string
  created?: string
  updated?: string
  instructions?: string | null
  // Project-scoped defaults used to preload Explorer
  defaultWebSearch?: boolean
  defaultModelId?: string | null
  // Pinned default libraries for new chats inside this project.
  // null/undefined = no pin (use user's last selection); array (possibly empty) = pin this set.
  defaultEnabledIndexIds?: string[] | null
  // Legacy format: list of URL strings (for backward compatibility)
  trustedSourceUrls?: string[]
  // New format: list of trusted source objects with metadata
  trustedSources?: TrustedSourceRef[]
  // Sharing
  participants?: Participant[]
  currentUserRole?: ParticipantRole
  ownerUserId?: string
  // Notification status
  hasUnviewedNotification?: boolean
  invitationNotification?: {
    invitedBy: string
    invitedByDisplayName: string
    role: ParticipantRole
  }
}

export type EmbeddingStatus = "pending" | "processing" | "completed" | "failed"

export type ProjectFile = {
  filename: string
  uploaded_at: string
  embedding_status?: EmbeddingStatus
  embedding_error?: string
  /** True if file is missing from blob storage and needs full re-upload */
  needs_reupload?: boolean
  /** True if file is indexed in a valid vector store (searchable) */
  in_vector_store?: boolean
  /** False when this file type has no safe provider retrieval representation */
  vector_store_eligible?: boolean
}

/**
 * Build the URL for a project file's raw bytes -- served inline
 * (`as_attachment=False`) so it works for both the document viewer preview
 * and a client-side forced download (see DownloadButton/useCitationFileDownload).
 * Mirrors `getCitationFilePath` in api/client/citations.ts.
 */
export function getProjectFileContentUrl(projectId: string, filename: string): string {
  return `/api/projects/${encodeURIComponent(projectId)}/files/content/${encodeURIComponent(filename)}`
}

export type EmbeddingStatusResponse = {
  total: number
  pending: number
  processing: number
  completed: number
  failed: number
  is_complete: boolean
  files: {
    filename: string
    status: EmbeddingStatus
    error?: string
  }[]
}

export const ProjectService = {
  async listProjects(sessionType?: string): Promise<Project[]> {
    try {
      logger.debug("ProjectService.listProjects: requesting /projects", { sessionType })
      const projects = (await api.get("/projects", {
        params: sessionType ? { session_type: sessionType } : undefined,
      })) as Project[]
      logger.debug("ProjectService.listProjects: received response", {
        count: Array.isArray(projects) ? projects.length : 0,
      })
      return projects
    } catch (error) {
      logger.error("ProjectService.listProjects: request failed", error as Error)
      throw error
    }
  },
  async createProject(payload: { project_id?: string; name?: string; session_type: string }) {
    try {
      logger.debug("ProjectService.createProject: posting /projects", { payload })
      const project = (await api.post("/projects", payload)) as Project
      logger.debug("ProjectService.createProject: created", { id: project.id })
      return project
    } catch (error) {
      logger.error("ProjectService.createProject: request failed", error as Error)
      throw error
    }
  },
  async updateProject(
    project_id: string,
    data: Partial<
      Pick<
        Project,
        | "name"
        | "instructions"
        | "defaultWebSearch"
        | "defaultModelId"
        | "defaultEnabledIndexIds"
        | "trustedSources"
        | "trustedSourceUrls"
      >
    >
  ) {
    logger.debug("ProjectService.updateProject: put /projects/:id", { project_id, data })
    return (await api.put(`/projects/${project_id}`, data)) as Project
  },
  async deleteProject(project_id: string) {
    logger.debug("ProjectService.deleteProject: delete /projects/:id", { project_id })
    return (await api.delete(`/projects/${project_id}`)) as { status: string }
  },
  async listFiles(project_id: string) {
    logger.debug("ProjectService.listFiles: get /projects/:id/files", { project_id })
    return (await api.get(`/projects/${project_id}/files`)) as ProjectFile[]
  },
  async uploadFiles(project_id: string, files: File[]) {
    logger.debug("ProjectService.uploadFiles: post /projects/:id/files", {
      project_id,
      count: files.length,
    })
    const formData = new FormData()
    files.forEach((f) => formData.append("files", f))
    return (await api.post(`/projects/${project_id}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })) as { files: ProjectFile[] }
  },
  async deleteFile(project_id: string, filename: string) {
    logger.debug("ProjectService.deleteFile: delete /projects/:id/files", { project_id, filename })
    return (await api.delete(`/projects/${project_id}/files`, {
      params: { filename },
    })) as { success: boolean }
  },
  async getEmbeddingStatus(project_id: string) {
    logger.debug("ProjectService.getEmbeddingStatus: get /projects/:id/embedding-status", {
      project_id,
    })
    return (await api.get(`/projects/${project_id}/embedding-status`)) as EmbeddingStatusResponse
  },

  async optimizeInstructions(instructions: string) {
    logger.debug("ProjectService.optimizeInstructions: post /projects/optimize-instructions")
    return (await api.post("/projects/optimize-instructions", { instructions })) as {
      optimized_instructions: string
    }
  },

  // Participant management
  async listParticipants(project_id: string): Promise<ParticipantsResponse> {
    logger.debug("ProjectService.listParticipants: get /projects/:id/participants", { project_id })
    return (await api.get(`/projects/${project_id}/participants`)) as ParticipantsResponse
  },

  async addParticipant(
    project_id: string,
    participant: {
      oid: string
      displayName: string
      type: "user" | "group"
      role: ParticipantRole
    }
  ): Promise<{ success: boolean; participant: Participant }> {
    logger.debug("ProjectService.addParticipant: post /projects/:id/participants", {
      project_id,
      participant,
    })
    return (await api.post(`/projects/${project_id}/participants`, participant)) as {
      success: boolean
      participant: Participant
    }
  },

  async updateParticipantRole(
    project_id: string,
    participant_oid: string,
    role: ParticipantRole
  ): Promise<{ success: boolean; role: ParticipantRole }> {
    logger.debug("ProjectService.updateParticipantRole: put /projects/:id/participants/:oid", {
      project_id,
      participant_oid,
      role,
    })
    return (await api.put(`/projects/${project_id}/participants/${participant_oid}`, {
      role,
    })) as { success: boolean; role: ParticipantRole }
  },

  async removeParticipant(
    project_id: string,
    participant_oid: string
  ): Promise<{ success: boolean }> {
    logger.debug("ProjectService.removeParticipant: delete /projects/:id/participants/:oid", {
      project_id,
      participant_oid,
    })
    return (await api.delete(`/projects/${project_id}/participants/${participant_oid}`)) as {
      success: boolean
    }
  },

  async transferOwnership(
    project_id: string,
    new_owner_oid: string
  ): Promise<{ success: boolean; newOwnerOid: string; message: string }> {
    logger.debug("ProjectService.transferOwnership: post /projects/:id/transfer-ownership", {
      project_id,
      new_owner_oid,
    })
    return (await api.post(`/projects/${project_id}/transfer-ownership`, {
      newOwnerOid: new_owner_oid,
    })) as { success: boolean; newOwnerOid: string; message: string }
  },

  async markProjectViewed(project_id: string): Promise<{ success: boolean }> {
    logger.debug("ProjectService.markProjectViewed: post /projects/:id/mark-viewed", { project_id })
    return (await api.post(`/projects/${project_id}/mark-viewed`)) as { success: boolean }
  },
}
